import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// «شدتِ روشناییِ اتاق» — هر اتاق عددِ روشناییِ خودش را دارد (۱۰٪ تا ۱۰۰٪)، که کاربر از پنلِ سقفِ همان
// اتاق (تبِ «نور») تنظیم می‌کند و در ceilingStyle.lightPct ذخیره می‌شود. ۷۰٪ دقیقاً همان نورِ فعلیِ صحنه
// است؛ پس ۱۰۰٪ یعنی حدودِ ۱٫۴۳ برابرِ نورِ فعلی و ۱۰٪ یعنی حدودِ ۰٫۱۴ برابر.
//
// قانونِ درها (خواسته‌ی کاربر):
//   • اتاقِ «پنجره‌دار»: هر درِ بازِ روی دیوارهای اتاق +۱۰٪؛ اگر روشنایی از قبل ۹۰٪ یا بیشتر باشد، مستقیم
//     ۱۰۰٪ می‌شود.
//   • اتاقِ «بدونِ پنجره»: هر درِ باز +۲۵٪ (حداکثر ۱۰۰٪).
//   • چند درِ باز جمع می‌شوند؛ با بسته‌شدنِ در، روشنایی به عددِ تنظیم‌شده‌ی کاربر برمی‌گردد.
//   • درِ روی دیوارِ مشترک برای «هر دو» اتاقِ دو طرفش حساب می‌شود.
//   • «بازشو» (فضای باز بدونِ در) مثلِ یک درِ همیشه‌باز حساب می‌شود.
//
// قانونِ پنجره‌ها: هر پنجره‌ی باز، به نسبتِ مساحتِ خودش به مساحتِ یک درِ استاندارد (۹۰×۲۱۰)، همان +۱۰٪ِ
// درِ اتاقِ پنجره‌دار را اضافه می‌کند — پنجره‌ی ۱۲۰×۱۳۰ حدودِ +۸٪، پنجره‌ی ۲۰۰×۱۵۰ حدودِ +۱۶٪ (حداکثر ۱۰۰٪).
// با بسته‌شدنِ پنجره برمی‌گردد. پنجره‌ی «ثابت» باز نمی‌شود، پس اثری ندارد.
//
// پیاده‌سازی: نورهای صحنه سراسری‌اند (hemisphere/ambient/environment)، پس روشناییِ «هر اتاق» را نمی‌شود
// با خودِ نورها جدا کرد. به‌جایش به شیدرِ همه‌ی متریال‌ها یک قدمِ کوچک اضافه می‌شود: هر پیکسل با
// موقعیتِ جهانیِ خودش پیدا می‌کند داخلِ کدام اتاق است و نورِ نهایی‌اش (outgoingLight، قبل از tone mapping)
// در ضریبِ همان اتاق ضرب می‌شود. کف/سقف/رویه‌ی دیوارها/لنگه‌ی درها همه خودبه‌خود ضریبِ اتاقِ خودشان را
// می‌گیرند، و از لای درِ باز، اتاقِ کناری با روشناییِ خودش دیده می‌شود. بیرون از همه‌ی اتاق‌ها ضریب ۱ است.

export const DEFAULT_ROOM_LIGHT_PCT = 70;
export const MIN_ROOM_LIGHT_PCT = 10;
export const MAX_ROOM_LIGHT_PCT = 100;

const WINDOW_ROOM_STEP = 10;
const WINDOW_ROOM_JUMP_FROM = 90;
const NO_WINDOW_ROOM_STEP = 25;
const REFERENCE_DOOR_AREA_CM2 = 90 * 210;

// فاصله‌ی مجاز (سانتی‌متر) از لبه‌ی اتاق تا یک در/پنجره «روی دیوارِ این اتاق» حساب شود — کمی بیشتر
// از نصفِ ضخامتِ دیوار، برای خطای اعشاریِ اسنپِ مغناطیسی
const EDGE_TOLERANCE_CM = 20;

// تکه‌های فضاها (هر فضا ممکن است چند تکه باشد؛ کنارِ دیوارِ منحنی/مورب ده‌ها تکه‌ی ذوزنقه‌ای) — در یک بافتِ
// داده نگه داشته می‌شوند، نه uniform (تعدادِ uniform روی موبایل محدود است)
const MAX_ROOMS = 512;
// سرعتِ نرم‌شدنِ تغییرِ روشنایی (باز/بسته‌شدنِ در، کشیدنِ اسلایدر) — تقریباً نیم‌ثانیه
const FADE_SPEED = 5;

export function clampLightPct(value) {
  const n = Number.isFinite(value) ? value : DEFAULT_ROOM_LIGHT_PCT;
  return Math.min(MAX_ROOM_LIGHT_PCT, Math.max(MIN_ROOM_LIGHT_PCT, n));
}

// فضای چندتکه (اتاقِ بزرگی که اتاقِ دیگری داخلش است): لبه‌ی هر کدام از تکه‌ها
function isOnRoomEdge(room, x, z, tol = EDGE_TOLERANCE_CM) {
  const pieces = room.rects && room.rects.length ? room.rects : [room];
  return pieces.some((p) => isOnRectEdge(p, x, z, tol));
}

function isOnRectEdge(rect, x, z, tol) {
  const minX = Math.min(rect.x1, rect.x2);
  const maxX = Math.max(rect.x1, rect.x2);
  const minZ = Math.min(rect.y1, rect.y2);
  const maxZ = Math.max(rect.y1, rect.y2);
  if (x < minX - tol || x > maxX + tol || z < minZ - tol || z > maxZ + tol) return false;
  return (
    Math.abs(x - minX) <= tol || Math.abs(x - maxX) <= tol || Math.abs(z - minZ) <= tol || Math.abs(z - maxZ) <= tol
  );
}

// items: { doors: [{id,x,z}], windows: [{id,x,z,areaCm2}], openings: [{id,x,z}] } (سانتی‌متر، مختصاتِ پلان)
// baseLightPct(rect) → عددِ تنظیم‌شده‌ی کاربر برای آن اتاق
// خروجی: Map(roomId → { base, effective, hasWindow, openCount, openWindowCount })
export function computeRoomLightLevels(rooms, items, openDoorIds, baseLightPct, openWindowIds) {
  const result = new Map();
  (rooms || []).forEach((rect) => {
    const base = clampLightPct(baseLightPct(rect));
    const roomWindows = items.windows.filter((w) => isOnRoomEdge(rect, w.x, w.z));
    const hasWindow = roomWindows.length > 0;
    const openDoors = items.doors.filter((d) => openDoorIds?.has(d.id) && isOnRoomEdge(rect, d.x, d.z)).length;
    const openings = items.openings.filter((o) => isOnRoomEdge(rect, o.x, o.z)).length;
    const openCount = openDoors + openings;
    let effective = base;
    if (hasWindow) {
      for (let i = 0; i < openCount; i += 1) {
        effective = effective >= WINDOW_ROOM_JUMP_FROM ? MAX_ROOM_LIGHT_PCT : effective + WINDOW_ROOM_STEP;
      }
    } else {
      effective += NO_WINDOW_ROOM_STEP * openCount;
    }
    const openWindows = roomWindows.filter((w) => openWindowIds?.has(w.id));
    openWindows.forEach((w) => {
      effective += WINDOW_ROOM_STEP * (Math.max(0, w.areaCm2 || 0) / REFERENCE_DOOR_AREA_CM2);
    });
    result.set(rect.id, {
      base,
      effective: Math.min(MAX_ROOM_LIGHT_PCT, effective),
      hasWindow,
      openCount,
      openWindowCount: openWindows.length,
    });
  });
  return result;
}

// ---------------------------------------------------------------------------------------------------
// شیدر

// uniformهای مشترک بینِ همه‌ی متریال‌ها — هر متریال به «همین» شیءها وصل می‌شود، پس هر تغییر (باز شدنِ
// در، کشیدنِ اسلایدر) بدونِ کامپایلِ دوباره فوراً روی همه اعمال می‌شود
// بافتِ داده: برای هر تکه ۳ پیکسل — (کادر: minX, minZ, maxX, maxZ) (ذوزنقه: xl0, xl1, xr0, xr1) (ضریب)
const roomData = new Float32Array(MAX_ROOMS * 3 * 4);
const roomTex = new THREE.DataTexture(roomData, MAX_ROOMS * 3, 1, THREE.RGBAFormat, THREE.FloatType);
roomTex.magFilter = THREE.NearestFilter;
roomTex.minFilter = THREE.NearestFilter;
roomTex.generateMipmaps = false;
roomTex.needsUpdate = true;
const roomUniforms = {
  uRoomCount: { value: 0 },
  uRoomTex: { value: roomTex },
};

const VERTEX_DECL = '#include <common>\nvarying vec3 vRoomLightPos;';
const VERTEX_BODY = '#include <project_vertex>\nvRoomLightPos = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;';
const FRAGMENT_DECL = `#include <common>
varying vec3 vRoomLightPos;
uniform int uRoomCount;
uniform highp sampler2D uRoomTex;
float khRoomLightFactor( vec2 p ) {
	for ( int i = 0; i < ${MAX_ROOMS}; i ++ ) {
		if ( i >= uRoomCount ) break;
		vec4 r = texelFetch( uRoomTex, ivec2( i * 3, 0 ), 0 );
		if ( p.x >= r.x && p.x <= r.z && p.y >= r.y && p.y <= r.w ) {
			// شکلِ واقعیِ تکه (ذوزنقه کنارِ دیوارِ مورب/منحنی) — نه فقط کادرش، وگرنه رویه‌ی بیرونیِ دیوار پله‌پله نورِ اتاق را می‌گرفت
			vec4 t = texelFetch( uRoomTex, ivec2( i * 3 + 1, 0 ), 0 );
			float k = clamp( ( p.y - r.y ) / max( r.w - r.y, 1e-5 ), 0.0, 1.0 );
			if ( p.x >= mix( t.x, t.y, k ) && p.x <= mix( t.z, t.w, k ) ) return texelFetch( uRoomTex, ivec2( i * 3 + 2, 0 ), 0 ).x;
		}
	}
	return 1.0;
}`;
const FRAGMENT_BODY = 'outgoingLight *= khRoomLightFactor( vRoomLightPos.xz );\n#include <opaque_fragment>';

// «نرم‌کردنِ سایه‌روشن» (فعلاً فقط رویه‌ی دیوارها، با userData.khShadeSoften): نورِ نهایی را به همان
// اندازه به سمتِ رنگِ تختِ بدونِ نور (diffuseColor = رنگ × عکس) می‌برد — ۰ = نورپردازیِ کامل، ۱ = کاملاً تخت
// (مثلِ دیوارهای قدیمیِ meshBasicMaterial)، ۰٫۵ = سایه‌روشن نصف
function softenLine(soften) {
  return `outgoingLight = mix( outgoingLight, diffuseColor.rgb, ${soften.toFixed(3)} );\n`;
}

function patchShader(shader, soften = 0) {
  // idempotent: اگر به هر دلیلی دو بار صدا زده شود، دوباره تزریق نمی‌کند (تعریفِ تکراری = خطای کامپایل)
  if (shader.fragmentShader.includes('khRoomLightFactor')) return;
  shader.uniforms.uRoomCount = roomUniforms.uRoomCount;
  shader.uniforms.uRoomTex = roomUniforms.uRoomTex;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', VERTEX_DECL)
    .replace('#include <project_vertex>', VERTEX_BODY);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', FRAGMENT_DECL)
    .replace('#include <opaque_fragment>', (soften > 0 ? softenLine(soften) : '') + FRAGMENT_BODY);
}

function isPatchable(material) {
  // userData.khNoRoomLight: متریال‌هایی که عمداً نباید تاریک/روشن شوند (مثلاً خطوطِ اندازه‌ی عینکِ مهندسی)
  if (!material || material.userData?.khNoRoomLight) return false;
  return material.isMeshStandardMaterial || material.isMeshBasicMaterial;
}

// متریال را (یک بار) به روشناییِ اتاق وصل می‌کند؛ اگر قبلاً onBeforeCompileِ دیگری داشته باشد (مثلاً
// «رنگ‌آمیزیِ هوشمند»ِ کاشی/آجر در pbrTextures.jsx) آن را حفظ می‌کند و بعدش اجرا می‌شود.
// نشانه‌ی «وصل‌شده» روی خودِ تابعِ wrapper است، نه در material.userData — چون React (R3F) ممکن است
// userData را با یک شیءِ تازه جایگزین کند و آن‌وقت نشانه گم می‌شد و متریال دو بار وصل می‌شد
function ensurePatched(material) {
  if (!isPatchable(material)) return;
  const current = material.onBeforeCompile;
  if (current && current.khRoomLight) return;

  const soften = Math.min(1, Math.max(0, Number(material.userData?.khShadeSoften) || 0));
  const previousKey = material.customProgramCacheKey;
  const wrapper = function khRoomLightOnBeforeCompile(shader, renderer) {
    if (typeof current === 'function') current.call(this, shader, renderer);
    patchShader(shader, soften);
  };
  wrapper.khRoomLight = true;
  material.onBeforeCompile = wrapper;
  material.customProgramCacheKey = function khRoomLightKey() {
    const base = typeof previousKey === 'function' ? previousKey.call(this) : '';
    return `${base}|kh-room-light|soft${soften}`;
  };
  material.needsUpdate = true;
}

// داخلِ <Canvas> قرار می‌گیرد: متریال‌های تازه را وصل می‌کند و ضریبِ هر اتاق را نرم به هدفش می‌رساند.
// rooms: roomGroupRects (سانتی‌متر). levels: خروجیِ computeRoomLightLevels. unitScale: سانتی‌متر → واحدِ
// صحنه (همان M در Create3D)
// هر تکه‌ی هر فضا یک ردیف در شیدر (به ترتیبِ فضاها — کوچک‌ترها اول، پس اتاقِ داخلی برنده است)
function pieceList(rooms) {
  const out = [];
  (rooms || []).forEach((r) => {
    (r.rects && r.rects.length ? r.rects : [r]).forEach((p) => {
      const y1 = Math.min(p.y1, p.y2);
      const y2 = Math.max(p.y1, p.y2);
      const trap = p.xl0 !== undefined;
      const xl0 = trap ? p.xl0 : Math.min(p.x1, p.x2);
      const xl1 = trap ? p.xl1 : xl0;
      const xr0 = trap ? p.xr0 : Math.max(p.x1, p.x2);
      const xr1 = trap ? p.xr1 : xr0;
      out.push({ id: r.id, x1: Math.min(xl0, xl1), x2: Math.max(xr0, xr1), y1, y2, xl0, xl1, xr0, xr1 });
    });
  });
  return out.slice(0, MAX_ROOMS);
}

export function RoomLightingController({ rooms, levels, unitScale }) {
  const { scene } = useThree();
  const currentRef = useRef(new Map());
  const frameRef = useRef(0);

  // مستطیلِ اتاق‌ها به واحدِ صحنه (x → x، y پلان → z صحنه)
  useEffect(() => {
    const list = pieceList(rooms);
    list.forEach((p, i) => {
      const o = i * 12;
      roomData.set([p.x1 * unitScale, p.y1 * unitScale, p.x2 * unitScale, p.y2 * unitScale], o);
      roomData.set([p.xl0 * unitScale, p.xl1 * unitScale, p.xr0 * unitScale, p.xr1 * unitScale], o + 4);
    });
    roomTex.needsUpdate = true;
    roomUniforms.uRoomCount.value = list.length;
  }, [rooms, unitScale]);

  // وقتی این کامپوننت از صحنه برداشته شود، ضریب‌ها به حالتِ خنثی برمی‌گردند
  useEffect(
    () => () => {
      roomUniforms.uRoomCount.value = 0;
    },
    []
  );

  useFrame((_, delta) => {
    // متریال‌های تازه (بعد از هر تغییرِ نوع/جنس، یک متریالِ نو ساخته می‌شود) — هر چند فریم یک بار
    frameRef.current += 1;
    if (frameRef.current % 15 === 1) {
      scene.traverse((obj) => {
        if (!obj.isMesh) return;
        if (Array.isArray(obj.material)) obj.material.forEach(ensurePatched);
        else ensurePatched(obj.material);
      });
    }

    const t = Math.min(1, delta * FADE_SPEED);
    (rooms || []).forEach((rect) => {
      const target = (levels?.get(rect.id)?.effective ?? DEFAULT_ROOM_LIGHT_PCT) / DEFAULT_ROOM_LIGHT_PCT;
      const prev = currentRef.current.has(rect.id) ? currentRef.current.get(rect.id) : target;
      const next = Math.abs(target - prev) < 0.001 ? target : prev + (target - prev) * t;
      currentRef.current.set(rect.id, next);
    });
    let changed = false;
    pieceList(rooms).forEach((piece, i) => {
      const f = currentRef.current.get(piece.id) ?? 1;
      if (roomData[i * 12 + 8] !== Math.fround(f)) {
        roomData[i * 12 + 8] = f;
        changed = true;
      }
    });
    if (changed) roomTex.needsUpdate = true;
  });

  return null;
}